import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Plug, Layers, FlaskConical, Rocket, LogOut, TrendingUp,
  PlusCircle, Wallet, ScrollText, Shield, Star, Menu, X
} from "lucide-react";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/brokers", label: "Brokers", icon: Plug },
  { to: "/strategies", label: "Strategies", icon: Layers },
  
  { to: "/builder", label: "Strategy Builder", icon: PlusCircle },
  { to: "/backtest", label: "Backtesting", icon: FlaskConical },
  { to: "/autotrade", label: "Auto Trading", icon: Rocket },
  { to: "/positions", label: "Live Positions", icon: Wallet },
  { to: "/logs", label: "Execution Logs", icon: ScrollText },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen flex bg-slate-50 text-slate-900">
      <aside className={`w-60 shrink-0 border-r border-indigo-900/40 bg-gradient-to-b from-slate-950 via-indigo-950 to-purple-950 text-white flex flex-col fixed inset-y-0 left-0 h-screen shadow-xl z-50 transition-transform duration-200 md:translate-x-0 ${mobileSidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="h-16 flex items-center gap-2 px-5 border-b border-indigo-400/20 bg-slate-950/40">
          <div className="w-8 h-8 bg-gradient-to-br from-cyan-400 via-blue-500 to-purple-600 flex items-center justify-center rounded-md shadow-lg shadow-blue-500/30">
            <TrendingUp className="w-5 h-5 text-white" />
          </div>
          <div className="leading-none">
            <div className="font-heading font-black text-lg tracking-tighter">KIRAN</div>
            <div className="text-[10px] font-mono tracking-[0.2em] text-cyan-300 uppercase">Algo Trading</div>
          </div>
        </div>
        <nav className="flex-1 py-3 overflow-y-auto">
          {(user?.role === "admin" ? [...NAV, { to: "/admin", label: "User Settings", icon: Shield }] : NAV).map(({ to, label, icon: Icon }) => {
            const active = loc.pathname === to;
            return (
              <Link
                key={to}
                to={to}
                onClick={() => setMobileSidebarOpen(false)}
                data-testid={`nav-${to.slice(1)}`}
                className={`flex items-center gap-3 px-5 py-2.5 text-sm transition-colors duration-150 border-l-2 ${
                  active
                    ? "border-cyan-400 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white font-semibold shadow-lg shadow-blue-900/30"
                    : "border-transparent text-slate-200 hover:bg-white/10 hover:text-white"
                }`}
              >
                <Icon className={`w-4 h-4 ${active ? "text-white" : "text-cyan-300"}`} />
                <span>{label}</span>
                {label === "Auto Trading" && (
                  <span className="ml-auto flex items-center gap-1.5 text-[9px] font-bold text-emerald-300">
                    <span className="kiran-live-dot" />
                  </span>
                )}
              </Link>
            );
          })}
        </nav>
        <div className="border-t border-indigo-400/20 bg-slate-950/35 p-4">
          <div className="text-xs font-mono text-slate-300 truncate mb-2" data-testid="sidebar-user-email">{user?.email}</div>
          <button
            data-testid="logout-btn"
            onClick={() => { logout(); nav("/login"); }}
            className="flex items-center gap-2 text-sm text-rose-300 hover:text-white transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>
      {mobileSidebarOpen && (
        <button
          type="button"
          aria-label="Close navigation menu"
          onClick={() => setMobileSidebarOpen(false)}
          className="fixed inset-0 bg-slate-950/60 z-40 md:hidden"
        />
      )}
      <main className="flex-1 min-w-0 ml-0 md:ml-60 min-h-screen">
        <div className="md:hidden h-14 sticky top-0 z-30 flex items-center gap-3 px-4 border-b border-slate-200 bg-white">
          <button
            type="button"
            data-testid="mobile-sidebar-toggle"
            aria-label={mobileSidebarOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={mobileSidebarOpen}
            onClick={() => setMobileSidebarOpen(open => !open)}
            className="inline-flex items-center justify-center w-10 h-10 rounded-lg text-slate-700 hover:bg-slate-100"
          >
            {mobileSidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <div className="font-heading font-black tracking-tight">KIRAN <span className="text-cyan-600">ALGO</span></div>
        </div>
        {children}
      </main>
    </div>
  );
}

export function PageHeader({ title, subtitle, right }) {
  return (
    <div className="min-h-16 border-b border-slate-200 bg-white flex flex-col items-stretch gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between md:px-8 sticky top-0 z-30">
      <div className="w-full min-w-0 sm:flex-1">
        <h1 className="font-heading text-xl font-bold tracking-tight leading-tight break-words">{title}</h1>
        {subtitle && <p className="text-xs text-slate-500 mt-1 leading-relaxed">{subtitle}</p>}
      </div>
      {right && <div className="w-full min-w-0 sm:w-auto sm:shrink-0">{right}</div>}
    </div>
  );
}
